import { ReactElement, useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { PALETTE } from './plotConstants';
import { useUpdateSession, useSetSession, useInvalidateSession } from '../../../../hooks/useSessions';
import { jStat } from 'jstat';
import { calculateLaps } from './lapUtils';
import { calculateYAxisParams } from '../../../../utils/plotUtils';

export default function AveragePlots(props: { configs: any[]; session: any }): ReactElement {
  const { configs, session } = props;
  const [normalizationType, setNormalizationType] = useState('full');

  // Initialize confidence level from localStorage or default to '68'
  const [confidenceLevel, setConfidenceLevel] = useState<string>(() => {
    const saved = localStorage.getItem('cdaConfidenceLevel');
    return saved || '68';
  });

  // Save confidence level to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cdaConfidenceLevel', confidenceLevel);
  }, [confidenceLevel]);

  // Check if session is a traverse type (1000 <= analysis_type < 2000)
  const isTraverseSession = session?.analysis_type >= 1000 && session?.analysis_type < 2000;

  // Initialize dataType from localStorage or default to 'laps'
  const [dataType, setDataType] = useState<'traverses' | 'laps'>(() => {
    const saved = localStorage.getItem('cdaDataType');
    if (saved && (saved === 'traverses' || saved === 'laps')) {
      // If saved value is 'traverses' but session is not traverse type, use 'laps'
      if (saved === 'traverses' && !isTraverseSession) {
        return 'laps';
      }
      return saved as 'traverses' | 'laps';
    }
    return 'laps';
  });

  // Save dataType to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cdaDataType', dataType);
  }, [dataType]);

  // Initialize xAxisType from localStorage or default to 'configs'
  const [xAxisType, setXAxisType] = useState<'configs' | 'timeOfDay'>(() => {
    const saved = localStorage.getItem('cdaXAxisType');
    if (saved && (saved === 'configs' || saved === 'timeOfDay')) {
      return saved as 'configs' | 'timeOfDay';
    }
    return 'configs';
  });

  // Save xAxisType to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cdaXAxisType', xAxisType);
  }, [xAxisType]);

  // Initialize yAxisScale from session.plotting_scales if it exists
  const initialYAxisScale = session?.plotting_scales?.cda ?? 'auto';
  const [yAxisScale, setYAxisScale] = useState<number | 'auto'>(initialYAxisScale);

  const updateSessionMutation = useUpdateSession(session?.id);
  const setSessionCache = useSetSession();
  const invalidateSession = useInvalidateSession(session?.id);

  // Handle Y-axis scale changes
  const handleYAxisScaleChange = (newScale: number | 'auto') => {
    setYAxisScale(newScale);

    if (!session?.id) return;

    // Initialize plotting_scales if it doesn't exist
    const plottingScales = session.plotting_scales || {};

    // Update plotting_scales based on the new scale
    if (newScale === 'auto') {
      // Remove the cda key if scale is 'auto'
      const { ...remainingScales } = plottingScales;
      const updatedSession = {
        ...session,
        plotting_scales: Object.keys(remainingScales).length > 0 ? remainingScales : {},
      };

      // Update cache immediately for UI responsiveness
      setSessionCache(updatedSession);

      // Update backend and refresh session
      updateSessionMutation.mutate(
        {
          plotting_scales: Object.keys(remainingScales).length > 0 ? remainingScales : {},
        },
        {
          onSuccess: () => {
            invalidateSession();
          },
        },
      );
    } else {
      // Set cda to the numerical value
      const updatedSession = {
        ...session,
        plotting_scales: {
          ...plottingScales,
          cda: newScale,
        },
      };

      // Update cache immediately for UI responsiveness
      setSessionCache(updatedSession);

      // Update backend and refresh session
      updateSessionMutation.mutate(
        {
          plotting_scales: {
            ...plottingScales,
            cda: newScale,
          },
        },
        {
          onSuccess: () => {
            invalidateSession();
          },
        },
      );
    }
  };

  const normalizationOptions = [
    { value: 'full', label: 'Full' },
    { value: 'without Body', label: 'Without Body' },
    { value: 'raw', label: 'Raw' },
  ];

  const yAxisScaleOptions = [
    { value: 0.01, label: '0.01' },
    { value: 0.02, label: '0.02' },
    { value: 0.05, label: '0.05' },
    { value: 0.1, label: '0.10' },
    { value: 0.2, label: '0.20' },
    { value: 'auto', label: 'Auto' },
  ];

  return (
    <div>
        <div className="print-only print-section-header">Data Quality</div>
        <div className="font-montserrat cr-mt-8 cr-mb-4 cr-font-semibold cr-text-[20px] screen-only">Data Quality</div>
      <div className="cr-mb-4 cr-flex cr-items-center cr-gap-4 cr-text-lg cr-text-[#03365D] screen-only">
        <span className="cr-font-medium">Normalisation:</span>
        {normalizationOptions.map((option) => (
          <label key={option.value} className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1">
            <input
              type="radio"
              name="averageNormalizationType"
              value={option.value}
              checked={normalizationType === option.value}
              onChange={(e) => setNormalizationType(e.target.value)}
              className="cr-m-0 cr-w-4 cr-h-4"
            />
            <span>{option.label}</span>
          </label>
        ))}
        <div className="cr-ml-8 cr-flex cr-items-center cr-gap-2">
          <span className="cr-font-medium">Y-Axis Scale:</span>
          <select
            value={yAxisScale}
            onChange={(e) => {
              const newValue = e.target.value === 'auto' ? 'auto' : parseFloat(e.target.value);
              handleYAxisScaleChange(newValue);
            }}
            className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-lg"
          >
            {yAxisScaleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="cr-mb-4 cr-flex cr-items-center cr-gap-4 cr-text-lg cr-text-[#03365D] screen-only">
        <span className="cr-font-medium">Lap Display:</span>
        <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1">
          <input
            type="radio"
            name="dataType"
            value="traverses"
            checked={dataType === 'traverses'}
            onChange={(e) => setDataType(e.target.value as 'traverses' | 'laps')}
            disabled={!isTraverseSession}
            className="cr-m-0 cr-w-4 cr-h-4"
          />
          <span className={!isTraverseSession ? 'cr-text-gray-400' : ''}>Traverses</span>
        </label>
        <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1">
          <input
            type="radio"
            name="dataType"
            value="laps"
            checked={dataType === 'laps'}
            onChange={(e) => setDataType(e.target.value as 'traverses' | 'laps')}
            className="cr-m-0 cr-w-4 cr-h-4"
          />
          <span>Laps</span>
        </label>
      </div>
      <div className="cr-mb-4 cr-flex cr-items-center cr-gap-4 cr-text-lg cr-text-[#03365D] screen-only">
        <span className="cr-font-medium">X-Axis:</span>
        <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1">
          <input
            type="radio"
            name="xAxisType"
            value="configs"
            checked={xAxisType === 'configs'}
            onChange={(e) => setXAxisType(e.target.value as 'configs' | 'timeOfDay')}
            className="cr-m-0 cr-w-4 cr-h-4"
          />
          <span>Configs</span>
        </label>
        <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1">
          <input
            type="radio"
            name="xAxisType"
            value="timeOfDay"
            checked={xAxisType === 'timeOfDay'}
            onChange={(e) => setXAxisType(e.target.value as 'configs' | 'timeOfDay')}
            className="cr-m-0 cr-w-4 cr-h-4"
          />
          <span>Time of Day</span>
        </label>
      </div>
      {xAxisType === 'configs' ? (
        <DataPlot
          normalizationType={normalizationType}
          configs={configs}
          yAxisScale={yAxisScale}
          confidenceLevel={confidenceLevel}
          setConfidenceLevel={setConfidenceLevel}
          session={session}
          dataType={dataType}
        />
      ) : (
        <TimeOfDayPlot
          normalizationType={normalizationType}
          configs={configs}
          yAxisScale={yAxisScale}
          session={session}
          dataType={dataType}
          confidenceLevel={confidenceLevel}
          setConfidenceLevel={setConfidenceLevel}
        />
      )}
    </div>
  );
}

const CDA_KEY_MAP: { [key: string]: string } = {
  full: 'CdAN_xG3',
  'without Body': 'CdAXWnNB_xG3',
  raw: 'CdA_xG3',
};

// Helper: Collect all traverse values from a config
function collectConfigTraverses(config: any, cdaKey: string): number[] {
  const traverses: number[] = [];
  for (const run of config.runs) {
    const runTraverses = run.traverse[cdaKey] || [];
    for (const cdaValue of runTraverses) {
      if (cdaValue) {
        traverses.push(cdaValue);
      }
    }
  }
  return traverses;
}

// Helper: Calculate baseline CI statistics from laps
function calculateBaselineCI(laps: number[], confidenceLevel: string): {
  mean: number;
  stdDev: number;
  ciLowerBound: number;
  ciUpperBound: number;
  zScore: number;
} | null {
  if (confidenceLevel === 'none' || laps.length < 3) {
    return null;
  }

  const n = laps.length;
  const df = n - 1;
  const mean = laps.reduce((sum, val) => sum + val, 0) / n;
  const variance = laps.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (n - 1);
  const stdDev = Math.sqrt(variance);
  const standardError = stdDev / Math.sqrt(n);

  const conf = Number(confidenceLevel) / 100;
  const alpha = 1 - conf;
  const critical = jStat.studentt.inv(1 - alpha / 2, df);
  const zScore = jStat.normal.inv(1 - alpha / 2, 0, 1);

  const ciMargin = critical * standardError;
  const ciLowerBound = mean - ciMargin;
  const ciUpperBound = mean + ciMargin;

  return { mean, stdDev, ciLowerBound, ciUpperBound, zScore };
}

// Helper: Calculate SD to use for a config's CI
function calculateConfigSD(configLaps: number[], baselineSD: number): number {
  const lapCount = configLaps.length;
  if (lapCount >= 3) {
    const cfgMean = configLaps.reduce((sum, val) => sum + val, 0) / lapCount;
    const cfgVar = configLaps.reduce((sum, val) => sum + Math.pow(val - cfgMean, 2), 0) / (lapCount - 1);
    const configSD = Math.sqrt(cfgVar);
    return Math.max(configSD, baselineSD);
  } else if (lapCount === 1) {
    return 2.5 * baselineSD;
  }
  return baselineSD;
}

// Helper: Generate legend annotation for a config
function createLegendAnnotation(
  config: any,
  index: number,
  xPosition: number,
  staggerLabels: boolean
): any {
  const description = `${config.letter}: ${config.description}`;
  const label = description.length > 23 ? `${description.slice(0, 23).trim()}...` : description;
  const color = PALETTE[index % PALETTE.length];
  const offsetY = staggerLabels ? (index % 2 === 0 ? -20 : 0) : -20;

  return {
    x: xPosition,
    strokeDashArray: '0 0 0 1',
    label: {
      orientation: 'horizontal',
      position: 'bottom',
      offsetY: offsetY,
      borderColor: color,
      style: { color: '#fff', background: color, fontSize: '14px', fontWeight: 400 },
      text: label,
    },
  };
}

function TimeOfDayPlot(props: {
  normalizationType: string;
  configs: any;
  yAxisScale: number | 'auto';
  session: any;
  dataType: 'traverses' | 'laps';
  confidenceLevel: string;
  setConfidenceLevel: (level: string) => void;
}) {
  const { normalizationType, configs, yAxisScale, session, dataType, confidenceLevel, setConfidenceLevel } = props;
  const cdaKey = CDA_KEY_MAP[normalizationType];
  const allYValues: number[] = [];
  const series: any[] = [];
  const colors: string[] = [];
  const laps: number[] = [];

  const isTraverseSession = session?.analysis_type >= 1000 && session?.analysis_type < 2000;
  const utcOffsetSeconds = Number(session?.utc_offset ?? 0);

  let configIndex = 0;
  for (const config of configs) {
    const cdaValues: Array<[number, number]> = [];
    const configTraverses = collectConfigTraverses(config, cdaKey);

    // Calculate laps for confidence interval (only for first config)
    if (configIndex === 0 && configTraverses.length > 0) {
      laps.push(...calculateLaps(configTraverses, isTraverseSession));
    }

    for (const run of config.runs) {
      const traverses = run.traverse[cdaKey] || [];
      const runTime = run.average?.GatesStartTs_tG3?.[0];
      if (runTime == null) continue;

      const baseTimeMs = (runTime + utcOffsetSeconds) * 1000;
      const valuesToPlot = dataType === 'laps'
        ? calculateLaps(traverses, isTraverseSession)
        : traverses.filter((v: number) => v);

      const spreadMs = 60000;
      for (let i = 0; i < valuesToPlot.length; i++) {
        const value = valuesToPlot[i];
        if (value) {
          const timeOffset = valuesToPlot.length > 1 ? (i / (valuesToPlot.length - 1)) * spreadMs : 0;
          cdaValues.push([baseTimeMs + timeOffset, value]);
          allYValues.push(value);
        }
      }
    }

    if (cdaValues.length > 0) {
      series.push({ name: config.letter, type: 'scatter', data: cdaValues });
      colors.push(PALETTE[configIndex % PALETTE.length]);
      configIndex++;
    }
  }

  // Calculate statistics for display text
  let confidenceIntervalText = '';
  let confidenceLevelText = '';
  if (laps.length > 1) {
    const mean = laps.reduce((sum, val) => sum + val, 0) / laps.length;
    const variance = laps.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (laps.length - 1);
    const stdDev = Math.sqrt(variance);
    const cv = mean !== 0 ? (stdDev / mean) * 100 : 0;
    const lapTypeText = isTraverseSession ? 'Laps (Traverse Pairs)' : 'Laps';
    confidenceIntervalText = `CdA Standard Deviation of ${lapTypeText}: ${stdDev.toFixed(4)}, CV: ${cv.toFixed(2)}%`;
  }

  // Calculate confidence intervals
  const baselineCI = calculateBaselineCI(laps, confidenceLevel);
  const configCIs: Array<{ upper: number; lower: number; color: string; minTime: number; maxTime: number }> = [];

  if (baselineCI) {
    const ciMargin = baselineCI.ciUpperBound - baselineCI.mean;
    const intervalLabel = confidenceLevel === '68'
      ? 'Typical Error of Mean' : `${confidenceLevel}% Confidence Interval of Mean`;
    confidenceLevelText =
      `${intervalLabel}: ${baselineCI.ciLowerBound.toFixed(3)} -
       ${baselineCI.ciUpperBound.toFixed(3)} (±${ciMargin.toFixed(4)})`;
  }

  if (baselineCI) {
    for (let i = 1; i < configs.length; i++) {
      const config = configs[i];
      const configMean = config.average[cdaKey]?.[0];
      if (!configMean) continue;

      const cfgTraverses = collectConfigTraverses(config, cdaKey);
      const cfgTimes: number[] = [];
      for (const run of config.runs) {
        const runTime = run.average?.GatesStartTs_tG3?.[0];
        if (runTime != null) cfgTimes.push((runTime + utcOffsetSeconds) * 1000);
      }

      const cfgLaps = calculateLaps(cfgTraverses, isTraverseSession);
      if (cfgLaps.length > 0 && cfgTimes.length > 0) {
        const sdToUse = calculateConfigSD(cfgLaps, baselineCI.stdDev);
        const configCIMargin = baselineCI.zScore * sdToUse / Math.sqrt(cfgLaps.length);
        let minCfgTime = Math.min(...cfgTimes);
        let maxCfgTime = Math.max(...cfgTimes);
        if (minCfgTime === maxCfgTime) {
          const padding = 5 * 60 * 1000;
          minCfgTime -= padding;
          maxCfgTime += padding;
        }
        configCIs.push({
          upper: configMean + configCIMargin,
          lower: configMean - configCIMargin,
          color: PALETTE[i % PALETTE.length],
          minTime: minCfgTime,
          maxTime: maxCfgTime,
        });
      }
    }
  }

  // Add config-specific CI lines as series
  const dashArray: number[] = Array(series.length).fill(0);
  const markerSizes: number[] = Array(series.length).fill(6);
  for (const ci of configCIs) {
    series.push({ name: '', type: 'line', data: [[ci.minTime, ci.upper], [ci.maxTime, ci.upper]] });
    colors.push(ci.color);
    dashArray.push(5);
    markerSizes.push(0);

    series.push({ name: '', type: 'line', data: [[ci.minTime, ci.lower], [ci.maxTime, ci.lower]] });
    colors.push(ci.color);
    dashArray.push(5);
    markerSizes.push(0);
  }

  const yAxisParams = calculateYAxisParams(allYValues, yAxisScale);

  // Calculate time range with padding
  const allTimes: number[] = series.flatMap((s: any) => s.data.map((d: any) => d[0] as number));
  const minTime = allTimes.length > 0 ? Math.min(...allTimes) : Date.now();
  const maxTime = allTimes.length > 0 ? Math.max(...allTimes) : Date.now();
  const timePadding = Math.max((maxTime - minTime) * 0.1, 5 * 60 * 1000);

  // Generate legend annotations
  const timeRange = (maxTime + timePadding) - (minTime - timePadding);
  const staggerLabels = configs.length > 4;
  const legendAnnotations = configs.map((config: any, index: number) => {
    const position = (minTime - timePadding) + (timeRange * (index + 0.5)) / configs.length;
    return createLegendAnnotation(config, index, position, staggerLabels);
  });

  const options = {
    chart: {
      height: 350,
      zoom: { enabled: true, allowMouseWheelZoom: false },
      animations: { enabled: false },
      toolbar: { show: true },
    },
    stroke: {
      dashArray: dashArray,
      width: 2,
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number) => val.toFixed(3),
      },
    },
    xaxis: {
      type: 'datetime' as const,
      min: minTime - timePadding,
      max: maxTime + timePadding,
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      tickAmount: yAxisParams.tickAmount,
      min: yAxisParams.yMin,
      max: yAxisParams.yMax,
      labels: { formatter: (val: number) => val.toFixed(3) },
      title: {
        text: 'CdA',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    annotations: {
      xaxis: legendAnnotations,
      yaxis: baselineCI ? [
        { y: baselineCI.ciUpperBound, borderColor: PALETTE[0], strokeDashArray: 5, borderWidth: 1 },
        { y: baselineCI.ciLowerBound, borderColor: PALETTE[0], strokeDashArray: 5, borderWidth: 1 },
      ] : [],
    },
    markers: { size: markerSizes },
    legend: { show: false },
    colors: colors,
  };

  return (
    <div>
      <Chart options={options} series={series} type="line" width="100%" height="380" />
      {confidenceIntervalText && (
        <div className="cr-mt-2 cr-flex cr-flex-col cr-gap-2 cr-text-left cr-text-lg cr-text-[#03365D]">
            <div>{confidenceIntervalText}</div>
            <div className="cr-flex cr-items-center cr-gap-2">
              <span>Show Confidence Interval:</span>
              <select
                value={confidenceLevel}
                onChange={(e) => setConfidenceLevel(e.target.value)}
                className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1"
              >
                <option value="none">None</option>
                <option value="68">Typical Error / 68%</option>
                <option value="80">80%</option>
                <option value="85">85%</option>
                <option value="90">90%</option>
                <option value="95">95%</option>
              </select>
            </div>
            {confidenceLevel !== 'none' && (
              <>
                <div>{confidenceLevelText}</div>
                <div>Dashed lines on plot show confidence. Further consistent repeats will narrow this interval</div>
              </>
            )}
        </div>
      )}
    </div>
  );
}

function DataPlot(props: { normalizationType: string; configs: any; yAxisScale: number | 'auto'; confidenceLevel: string; setConfidenceLevel: (level: string) => void; session: any; dataType: 'traverses' | 'laps' }) {
  const { normalizationType, configs, yAxisScale, confidenceLevel, setConfidenceLevel, session, dataType } = props;
  const cdaKey = CDA_KEY_MAP[normalizationType];
  const series: any[] = [];
  const colors: string[] = [];
  const labelColors: string[] = [];
  const sizes: number[] = [];
  const dashArray: number[] = [];
  const allYValues: number[] = [];
  const laps: number[] = [];
  const configCIs: Array<{ index: number; upper: number; lower: number; color: string }> = [];

  const isTraverseSession = session?.analysis_type >= 1000 && session?.analysis_type < 2000;

  let configIndex = 0;
  for (const config of configs) {
    const cdaValues: any[] = [];
    const configTraverses = collectConfigTraverses(config, cdaKey);

    // Calculate laps for confidence interval (only for first config)
    if (configIndex === 0 && configTraverses.length > 0) {
      laps.push(...calculateLaps(configTraverses, isTraverseSession));
    }

    const valuesToPlot = dataType === 'laps' ? calculateLaps(configTraverses, isTraverseSession) : configTraverses;
    const numValues = valuesToPlot.length;
    const spread = numValues > 0 ? 0.5 / numValues : 0;

    for (let i = 0; i < valuesToPlot.length; i++) {
      const value = valuesToPlot[i];
      if (value) allYValues.push(value);
      cdaValues.push([configIndex + 0.25 + i * spread, value]);
    }

    if (cdaValues.length > 0) {
      series.push({ name: config.letter, type: 'scatter', data: cdaValues });
      const average = config.average[cdaKey][0];
      series.push({
        name: '',
        type: 'line',
        data: [[configIndex + 0.25, average], [configIndex + 0.75, average]],
      });
      const color = PALETTE[configIndex % PALETTE.length];
      colors.push(color, color);
      labelColors.push(color, '#FFFFFF00');
      sizes.push(6, 0);
      dashArray.push(0, 0);
      configIndex++;
    }
  }

  const yAxisParams = calculateYAxisParams(allYValues, yAxisScale);

  // Calculate statistics for display text
  let confidenceIntervalText = '';
  let confidenceLevelText = '';
  if (laps.length > 1) {
    const mean = laps.reduce((sum, val) => sum + val, 0) / laps.length;
    const variance = laps.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (laps.length - 1);
    const stdDev = Math.sqrt(variance);
    const cv = mean !== 0 ? (stdDev / mean) * 100 : 0;
    const lapTypeText = isTraverseSession ? 'Laps (Traverse Pairs)' : 'Laps';
    confidenceIntervalText = `CdA Standard Deviation of ${lapTypeText}: ${stdDev.toFixed(4)}, CV: ${cv.toFixed(2)}%`;
  }

  // Calculate confidence intervals using shared helper
  const baselineCI = calculateBaselineCI(laps, confidenceLevel);

  if (baselineCI) {
    const ciMargin = baselineCI.ciUpperBound - baselineCI.mean;
    const intervalLabel = confidenceLevel === '68'
      ? 'Typical Error of Mean' : `${confidenceLevel}% Confidence Interval of Mean`;
    confidenceLevelText =
      `${intervalLabel}: ${baselineCI.ciLowerBound.toFixed(3)} -
       ${baselineCI.ciUpperBound.toFixed(3)} (±${ciMargin.toFixed(4)})`;

    // Calculate config-specific CIs
    for (let i = 1; i < configs.length; i++) {
      const config = configs[i];
      const configMean = config.average[cdaKey]?.[0];
      if (!configMean) continue;

      const cfgTraverses = collectConfigTraverses(config, cdaKey);
      const cfgLaps = calculateLaps(cfgTraverses, isTraverseSession);
      if (cfgLaps.length === 0) continue;

      const sdToUse = calculateConfigSD(cfgLaps, baselineCI.stdDev);
      const configCIMargin = baselineCI.zScore * sdToUse / Math.sqrt(cfgLaps.length);
      configCIs.push({
        index: i,
        upper: configMean + configCIMargin,
        lower: configMean - configCIMargin,
        color: PALETTE[i % PALETTE.length],
      });
    }

    // Add config-specific CI lines as series
    for (const ci of configCIs) {
      series.push({ name: '', type: 'line', data: [[ci.index + 0.25, ci.upper], [ci.index + 0.75, ci.upper]] });
      colors.push(ci.color);
      labelColors.push('#FFFFFF00');
      sizes.push(0);
      dashArray.push(5);

      series.push({ name: '', type: 'line', data: [[ci.index + 0.25, ci.lower], [ci.index + 0.75, ci.lower]] });
      colors.push(ci.color);
      labelColors.push('#FFFFFF00');
      sizes.push(0);
      dashArray.push(5);
    }
  }

  const staggerLabels = configs.length > 4;
  const options = {
    chart: { height: 350, zoom: { enabled: false }, animations: { enabled: false } },
    stroke: { dashArray: dashArray, width: 2 },
    tooltip: { enabled: false },
    xaxis: { tickAmount: 10, min: 0, max: configs.length, labels: { show: false } },
    yaxis: {
      tickAmount: yAxisParams.tickAmount,
      min: yAxisParams.yMin,
      max: yAxisParams.yMax,
      labels: { formatter: (val: number) => val.toFixed(3) },
      title: { text: 'CdA', style: { fontSize: '14px', fontWeight: 600 } },
    },
    annotations: {
      xaxis: configs.map((config: any, index: number) =>
        createLegendAnnotation(config, index, index + 0.5, staggerLabels)
      ),
      yaxis: baselineCI ? [
        { y: baselineCI.ciUpperBound, borderColor: PALETTE[0], strokeDashArray: 5, borderWidth: 1 },
        { y: baselineCI.ciLowerBound, borderColor: PALETTE[0], strokeDashArray: 5, borderWidth: 1 },
      ] : [],
    },
    markers: { size: sizes },
    legend: { show: false, markers: { fillColors: labelColors } },
    colors: colors,
  };
  return (
    <div>
      <Chart options={options} series={series} type="line" width="100%" height="380" />
      {confidenceIntervalText && (
        <div className="cr-mt-2 cr-flex cr-flex-col cr-gap-2 cr-text-left cr-text-lg cr-text-[#03365D]">
            <div>{confidenceIntervalText}</div>
            <div className="cr-flex cr-items-center cr-gap-2">
              <span>Show Confidence Interval:</span>
              <select
                value={confidenceLevel}
                onChange={(e) => setConfidenceLevel(e.target.value)}
                className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1"
              >
                <option value="none">None</option>
                <option value="68">Typical Error / 68%</option>
                <option value="80">80%</option>
                <option value="85">85%</option>
                <option value="90">90%</option>
                <option value="95">95%</option>
              </select>
            </div>
            {confidenceLevel !== 'none' && (
              <>
                <div>{confidenceLevelText}</div>
                <div>Dashed lines on plot show confidence. Further consistent repeats will narrow this interval</div>
              </>
            )}
        </div>
      )}
    </div>
  );
}
